import { RefObject, useEffect } from 'react'

import { emitEventOnIframe } from '@inno-tools/container-bridge'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores/windowStore'
import { SnapArea } from 'shared/types/index'

import { useInjectIframeScript } from './useInjectIframeScript'

interface UseInitWindowSnappingShortcutsProps {
  iframeRef: RefObject<HTMLIFrameElement>
  windowId: string
  isFocused: boolean
  enabled?: boolean
}

export function useInitWindowSnappingShortcuts({
  iframeRef,
  windowId,
  isFocused,
  enabled = true
}: UseInitWindowSnappingShortcutsProps) {
  const activeWindowId = useWindowStore(state => state.activeWindowId)

  const currentSnapArea = useWindowStore(
    state => state.windows.find(w => w.id === windowId)?.snapArea ?? null
  )

  const setSnapArea = useWindowStore(state => state.setSnapArea)

  useEffect(() => {
    if (activeWindowId !== windowId || !isFocused || !enabled) {
      return
    }

    function onKeyDown(event: KeyboardEvent) {
      let targetSnapArea = null

      if (!(event.metaKey && event.shiftKey)) {
        return
      }

      if (event.key === 'ArrowLeft') {
        if (currentSnapArea === SnapArea.TOP_RIGHT) {
          targetSnapArea = SnapArea.TOP_LEFT
        } else if (currentSnapArea === SnapArea.BOTTOM_RIGHT) {
          targetSnapArea = SnapArea.BOTTOM_LEFT
        } else {
          targetSnapArea = SnapArea.LEFT_HALF
        }
      }

      if (event.key === 'ArrowRight') {
        if (currentSnapArea === SnapArea.TOP_LEFT) {
          targetSnapArea = SnapArea.TOP_RIGHT
        } else if (currentSnapArea === SnapArea.BOTTOM_LEFT) {
          targetSnapArea = SnapArea.BOTTOM_RIGHT
        } else {
          targetSnapArea = SnapArea.RIGHT_HALF
        }
      }

      if (event.key === 'ArrowUp') {
        if (
          currentSnapArea === SnapArea.LEFT_HALF ||
          currentSnapArea === SnapArea.BOTTOM_LEFT
        ) {
          targetSnapArea = SnapArea.TOP_LEFT
        } else if (
          currentSnapArea === SnapArea.RIGHT_HALF ||
          currentSnapArea === SnapArea.BOTTOM_RIGHT
        ) {
          targetSnapArea = SnapArea.TOP_RIGHT
        } else {
          targetSnapArea = SnapArea.FULLSCREEN
        }
      }

      if (event.key === 'ArrowDown') {
        if (
          currentSnapArea === SnapArea.LEFT_HALF ||
          currentSnapArea === SnapArea.TOP_LEFT
        ) {
          targetSnapArea = SnapArea.BOTTOM_LEFT
        } else if (
          currentSnapArea === SnapArea.RIGHT_HALF ||
          currentSnapArea === SnapArea.TOP_RIGHT
        ) {
          targetSnapArea = SnapArea.BOTTOM_RIGHT
        } else {
          targetSnapArea = SnapArea.FULLSCREEN
        }
      }

      if (!targetSnapArea) {
        return
      }

      // Only set snap area if it's not fullscreen (fullscreen maps to maximize)
      if (targetSnapArea !== SnapArea.FULLSCREEN) {
        setSnapArea(windowId, targetSnapArea as SnapArea)
        emitEventOnIframe(
          {
            type: 'SNAP_WINDOW',
            data: { windowId: windowId, snapArea: targetSnapArea }
          },
          iframeRef.current as HTMLIFrameElement
        )
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [
    activeWindowId,
    currentSnapArea,
    isFocused,
    setSnapArea,
    windowId,
    enabled,
    iframeRef
  ])

  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !enabled) {
      return
    }

    if (!('__CONTAINER__' in iframeRef.current.contentWindow)) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(iframeRef.current.contentWindow['__CONTAINER__'] as any).snapArea =
      currentSnapArea
  }, [currentSnapArea, iframeRef, enabled])

  useInjectIframeScript(
    iframeRef,
    `
    // get parent reference from within the iframe
    const parent = window.parent

    if (!parent || !${enabled}) {
      return
    }

    window.addEventListener('keydown', (event) => {
      const currentSnapArea = window.__CONTAINER__?.snapArea
      let targetSnapArea = null

      if (!(event.metaKey && event.shiftKey)) {
        return
      }

      if (event.key === 'ArrowLeft') {
        if(currentSnapArea === 'topRight') {
         targetSnapArea = 'topLeft'
        } else if(currentSnapArea === 'bottomRight') {
          targetSnapArea = 'bottomLeft'
        } else {
         targetSnapArea = 'leftHalf'
        }
      }

      if (event.key === 'ArrowRight') {
        if(currentSnapArea === 'topLeft') {
          targetSnapArea = 'topRight'
        } else if(currentSnapArea === 'bottomLeft') {
          targetSnapArea = 'bottomRight'
        } else {
          targetSnapArea = 'rightHalf'
        }
      }


      if (event.key === 'ArrowUp') {
        if(currentSnapArea === 'leftHalf' || currentSnapArea === 'bottomLeft') {
          targetSnapArea = 'topLeft'
        } else if(currentSnapArea === 'rightHalf' || currentSnapArea === 'bottomRight') {
          targetSnapArea = 'topRight'
        } else {
          targetSnapArea = 'fullscreen'
        }
      }

      if (event.key === 'ArrowDown') {
        if(currentSnapArea === 'leftHalf' || currentSnapArea === 'topLeft') {
          targetSnapArea = 'bottomLeft'
        } else if(currentSnapArea === 'rightHalf' || currentSnapArea === 'topRight') {
          targetSnapArea = 'bottomRight'
        } else {
          targetSnapArea = 'fullscreen'
        }
      }

      if(!targetSnapArea) {
        return
      }

      parent.dispatchEvent(
          new CustomEvent('container-event', { detail: { type: 'SNAP_WINDOW', data: { windowId: '${windowId}', snapArea: targetSnapArea } } })
      )

      window.dispatchEvent(
          new CustomEvent('container-event', { detail: { type: 'SNAP_WINDOW', data: { windowId: '${windowId}', snapArea: targetSnapArea } } })
      )
    })
  `
  )
}
